import { DownOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Dropdown, Space } from "antd";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import "./emailEditor.css";
import {
  FONTS,
  MERGE_FIELD_BLOT,
  SIZES,
  isEditorEmpty,
  quillToHtml,
  registerEmailFormats,
  tokensToChips,
  typeBesideChips,
} from "./quillEmailSetup";

registerEmailFormats();

const Delta = Quill.import("delta");

const escapeHtml = (text) =>
  String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const optionsHtml = (defaultLabel, options) =>
  [
    `<option value="">${escapeHtml(defaultLabel)}</option>`,
    ...options.map(
      (option) =>
        `<option value="${escapeHtml(option.value)}">${escapeHtml(
          option.label,
        )}</option>`,
    ),
  ].join("");

// Quill's snow theme turns these selects into pickers and fills the empty ones
// (colours, alignment) with its defaults.
const toolbarHtml = () => `
  <span class="ql-formats">
    <select class="ql-font">${optionsHtml("Sans Serif", FONTS)}</select>
    <select class="ql-size">${optionsHtml("Normal", SIZES)}</select>
  </span>
  <span class="ql-formats">
    <button type="button" class="ql-bold" title="Bold"></button>
    <button type="button" class="ql-italic" title="Italic"></button>
    <button type="button" class="ql-underline" title="Underline"></button>
    <button type="button" class="ql-strike" title="Strikethrough"></button>
  </span>
  <span class="ql-formats">
    <select class="ql-color" title="Text colour"></select>
    <select class="ql-background" title="Highlight"></select>
  </span>
  <span class="ql-formats">
    <select class="ql-align" title="Align"></select>
    <button type="button" class="ql-list" value="ordered" title="Numbered list"></button>
    <button type="button" class="ql-list" value="bullet" title="Bulleted list"></button>
    <button type="button" class="ql-blockquote" title="Quote"></button>
  </span>
  <span class="ql-formats">
    <button type="button" class="ql-link" title="Link"></button>
    <button type="button" class="ql-clean" title="Remove formatting"></button>
  </span>
`;

// Inserts something at the cursor on a line of its own, so a signature (a block) never
// lands in the middle of a sentence. `insert(index)` must return the inserted length.
const insertOnOwnLine = (quill, insert) => {
  const range = quill.getSelection(true);
  let index = range.index;
  if (range.length > 0) quill.deleteText(index, range.length, "user");

  const [line, offset] = quill.getLine(index);
  const lineLength = line ? line.length() : 1;
  const hasContent = lineLength > 1;
  const textFollows = hasContent && offset < lineLength - 1;

  if (hasContent && offset > 0) {
    quill.insertText(index, "\n", "user");
    index += 1;
  }
  const insertedLength = insert(index);
  if (textFollows) {
    quill.insertText(index + insertedLength, "\n", "user");
  }
  quill.setSelection(index + insertedLength, 0, "user");
};

/*
  A Gmail-style compose box.

  defaultValue  HTML loaded once, on mount (remount with a `key` to load new content)
  onChange      called with clean HTML after every user edit
  fields        merge fields offered by "Insert field"; when given, {{tokens}} in
                defaultValue are shown as chips and stored back as tokens
  signature     { chip: true }  -> "Insert signature" drops a Signature chip (templates)
                { html: "..." } -> inserts that signature markup where the cursor is
                omitted         -> no signature button
  ref           { getHTML(), isEmpty(), insertField(key), insertSignature(), focus() }
*/
const EmailEditor = forwardRef(
  (
    {
      defaultValue,
      onChange,
      fields = [],
      signature,
      placeholder = "Write your email…",
      minHeight = 320,
    },
    ref,
  ) => {
    const toolbarHostRef = useRef(null);
    const editorHostRef = useRef(null);
    const quillRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const signatureRef = useRef(signature);

    useLayoutEffect(() => {
      onChangeRef.current = onChange;
      signatureRef.current = signature;
    }, [onChange, signature]);

    useEffect(() => {
      const toolbarHost = toolbarHostRef.current;
      const editorHost = editorHostRef.current;
      if (!toolbarHost || !editorHost) return undefined;

      const toolbar = toolbarHost.appendChild(document.createElement("div"));
      toolbar.innerHTML = toolbarHtml();
      const editorContainer = editorHost.appendChild(
        document.createElement("div"),
      );

      const quill = new Quill(editorContainer, {
        theme: "snow",
        placeholder,
        modules: {
          toolbar: { container: toolbar },
          clipboard: { matchVisual: false },
        },
      });
      quillRef.current = quill;
      typeBesideChips(quill);

      if (defaultValue) {
        const html = fields.length
          ? tokensToChips(defaultValue, fields)
          : defaultValue;
        quill.setContents(quill.clipboard.convert({ html }), "api");
      }
      quill.history.clear();

      const handleTextChange = (_delta, _oldDelta, source) => {
        if (source === "user" && onChangeRef.current) {
          onChangeRef.current(quillToHtml(quill));
        }
      };
      quill.on(Quill.events.TEXT_CHANGE, handleTextChange);

      return () => {
        quill.off(Quill.events.TEXT_CHANGE, handleTextChange);
        quillRef.current = null;
        toolbarHost.innerHTML = "";
        editorHost.innerHTML = "";
      };
      // The editor is created once; new content means a new `key` on the component
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const insertField = (key) => {
      const quill = quillRef.current;
      const field = fields.find((candidate) => candidate.key === key);
      if (!quill || !field) return;

      const range = quill.getSelection(true);
      const index = range.index;
      if (range.length > 0) quill.deleteText(index, range.length, "user");

      quill.insertEmbed(
        index,
        MERGE_FIELD_BLOT,
        { key: field.key, label: field.label },
        "user",
      );
      if (key === "application_url") {
        quill.formatText(index, 1, "link", "{{application_url}}", "user");
      }
      quill.setSelection(index + 1, 0, "user");
    };

    const insertSignature = () => {
      const quill = quillRef.current;
      const current = signatureRef.current;
      if (!quill || !current) return;

      if (current.chip) {
        insertOnOwnLine(quill, (index) => {
          quill.insertEmbed(
            index,
            MERGE_FIELD_BLOT,
            { key: "signature", label: "Signature" },
            "user",
          );
          return 1;
        });
      } else if (current.html) {
        insertOnOwnLine(quill, (index) => {
          const delta = quill.clipboard.convert({ html: current.html });
          quill.updateContents(new Delta().retain(index).concat(delta), "user");
          return delta.length();
        });
      }
    };

    useImperativeHandle(ref, () => ({
      getHTML: () => quillToHtml(quillRef.current),
      isEmpty: () => isEditorEmpty(quillRef.current),
      insertField,
      insertSignature,
      focus: () => quillRef.current?.focus(),
    }));

    const fieldMenu = {
      items: fields
        .filter((field) => field.key !== "signature")
        .map((field) => ({
          key: field.key,
          label: field.label,
          title: field.description,
        })),
      onClick: ({ key }) => insertField(key),
    };

    const showExtras = fieldMenu.items.length > 0 || Boolean(signature);

    return (
      <div
        className="email-editor"
        style={{ "--email-editor-min-height": `${minHeight}px` }}
      >
        {showExtras && (
          <div className="email-editor-extras">
            <Space wrap>
              {fieldMenu.items.length > 0 && (
                <Dropdown menu={fieldMenu} trigger={["click"]}>
                  <Button size="small">
                    Insert field <DownOutlined />
                  </Button>
                </Dropdown>
              )}
              {signature && (
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={insertSignature}
                >
                  Insert signature
                </Button>
              )}
            </Space>
          </div>
        )}
        <div ref={toolbarHostRef} />
        <div ref={editorHostRef} />
      </div>
    );
  },
);

EmailEditor.displayName = "EmailEditor";

export default EmailEditor;
